import { useMemo } from 'react'
import { Flag } from 'lucide-react'
import type { Question } from '../types'
import { cn } from './Button'

export interface QuestionnaireFormProps {
  questions: Question[]
  responses: Record<string, string | string[]>
  onChange: (inputId: string, value: string | string[]) => void
  onBlur?: (inputId: string) => void
  missingRequired?: string[]
  onFlag?: (question: Question, currentValue: string) => void
  flaggedFields?: Set<string>
}

function isAlwaysVisible(dependence: string) {
  return !dependence || dependence === 'NA' || dependence.toLowerCase() === 'na'
}

function getResponseValue(responses: Record<string, string | string[]>, inputId: string): string | string[] {
  return responses[inputId] ?? ''
}

export function isQuestionVisible(
  q: Question,
  responses: Record<string, string | string[]>
): boolean {
  if (isAlwaysVisible(q.dependence)) return true
  const parentValue = responses[q.dependence]
  if (parentValue == null || parentValue === '') return false

  const requiredValues = q.dependence_value
    .split(';')
    .map((v) => v.trim())
    .filter(Boolean)
  if (requiredValues.length === 0) return true

  if (Array.isArray(parentValue)) {
    return requiredValues.some((v) => parentValue.includes(v))
  }
  return requiredValues.includes(String(parentValue))
}

// A field groups all CSV rows that share the same input_id + question + input_type.
// The legacy questionnaire stores one row PER option, so dropdowns (y/n) and
// checkbox groups (check) span multiple rows whose `option` values are the choices.
interface Field {
  lead: Question
  options: string[]
}

function fieldKey(q: Question): string {
  // input_id alone is not unique: Header_1/Header_2 are reused for distinct
  // headings and MIBlood_info51 mixes a text and a y/n row. Mirror the Shiny
  // group_by(question, input_type, input_id, ...) so those stay separate.
  return `${q.input_id} ${q.question} ${q.input_type}`
}

export default function QuestionnaireForm({
  questions,
  responses,
  onChange,
  onBlur,
  missingRequired = [],
  onFlag,
  flaggedFields,
}: QuestionnaireFormProps) {
  // Collapse the per-option rows into one Field each, preserving original order
  // and aggregating the `option` values into the field's list of choices.
  const fields = useMemo(() => {
    const map = new Map<string, Field>()
    for (const q of questions) {
      const key = fieldKey(q)
      const existing = map.get(key)
      if (existing) {
        if (q.option) existing.options.push(q.option)
      } else {
        map.set(key, { lead: q, options: q.option ? [q.option] : [] })
      }
    }
    return Array.from(map.values())
  }, [questions])

  const fieldsByPage = useMemo(() => {
    const pageMap = new Map<string, Field[]>()
    for (const field of fields) {
      if (!isQuestionVisible(field.lead, responses)) continue
      const list = pageMap.get(field.lead.page) || []
      list.push(field)
      pageMap.set(field.lead.page, list)
    }
    return pageMap
  }, [fields, responses])

  const pageOrder = useMemo(() => {
    return Array.from(new Set(questions.map((q) => q.page)))
  }, [questions])

  const renderField = ({ lead: q, options }: Field) => {
    const hasError = missingRequired.includes(q.input_id) && q.required && isQuestionVisible(q, responses)

    if (q.input_type === 'header') {
      return (
        <div key={q.id} className="col-span-full">
          <h4 className="text-md font-semibold text-gray-800 mt-2">{q.question}</h4>
          {q.option && <p className="text-sm text-gray-500">{q.option}</p>}
        </div>
      )
    }

    if (q.input_type === 'header_2') {
      return (
        <div key={q.id} className="col-span-full">
          <h5 className="text-sm font-semibold text-gray-700 mt-1">{q.question}</h5>
        </div>
      )
    }

    const rawValue = getResponseValue(responses, q.input_id)
    const valueStr = Array.isArray(rawValue) ? rawValue.join('; ') : String(rawValue || '')
    const isFlagged = flaggedFields?.has(q.input_id)

    const label = (
      <div className="flex items-start justify-between gap-2 mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {q.question}
          {q.required && isQuestionVisible(q, responses) && <span className="text-red-500 ml-1">*</span>}
        </label>
        {onFlag && (
          <button
            type="button"
            onClick={() => onFlag(q, valueStr)}
            title={
              isFlagged
                ? 'Feedback submitted — click to flag again'
                : 'Flag an AI extraction issue with this answer'
            }
            className={cn(
              'flex-shrink-0 rounded p-1 transition-colors',
              isFlagged ? 'text-orange-600 hover:text-orange-700' : 'text-gray-300 hover:text-gray-500'
            )}
            aria-label="Flag AI extraction issue"
          >
            <Flag className="w-4 h-4" fill={isFlagged ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>
    )

    const containerClass = cn(
      'p-4 border rounded-lg bg-white transition-colors',
      hasError ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-200'
    )

    if (q.input_type === 'textarea' || q.input_type === 'text_big') {
      return (
        <div key={q.id} className={cn(containerClass, 'col-span-full')}>
          {label}
          <textarea
            value={(getResponseValue(responses, q.input_id) as string) || ''}
            onChange={(e) => onChange(q.input_id, e.target.value)}
            onBlur={() => onBlur?.(q.input_id)}
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
            rows={4}
          />
        </div>
      )
    }

    if (q.input_type === 'y/n') {
      // Choices come from the grouped option rows (e.g. Yes/No, but also
      // Yes/Slow/No or temperature scales). Fall back to Yes/No when absent.
      const choices = options.length > 0 ? options : ['Yes', 'No']
      return (
        <div key={q.id} className={containerClass}>
          {label}
          <select
            value={(getResponseValue(responses, q.input_id) as string) || ''}
            onChange={(e) => onChange(q.input_id, e.target.value)}
            onBlur={() => onBlur?.(q.input_id)}
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">— Select —</option>
            {choices.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        </div>
      )
    }

    if (q.input_type === 'check') {
      const selected = (getResponseValue(responses, q.input_id) as string[]) || []

      const toggleOption = (option: string) => {
        const next = selected.includes(option)
          ? selected.filter((v) => v !== option)
          : [...selected, option]
        onChange(q.input_id, next)
      }

      return (
        <div key={q.input_id} className={cn(containerClass, 'col-span-full md:col-span-2')}>
          {label}
          <div className="space-y-2 mt-2">
            {options.map((option) => (
              <label key={option} className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleOption(option)}
                  onBlur={() => onBlur?.(q.input_id)}
                  className="mt-1 h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                />
                <span className="text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div key={q.id} className={containerClass}>
        {label}
        <input
          type="text"
          value={(getResponseValue(responses, q.input_id) as string) || ''}
          onChange={(e) => onChange(q.input_id, e.target.value)}
          onBlur={() => onBlur?.(q.input_id)}
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {pageOrder.map((page) => {
        const pageFields = fieldsByPage.get(page)
        if (!pageFields || pageFields.length === 0) return null
        return (
          <section key={page} className="border-t pt-6 first:border-t-0 first:pt-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{page}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{pageFields.map((field) => renderField(field))}</div>
          </section>
        )
      })}
    </div>
  )
}
